import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  KeyboardAvoidingView,
  ScrollView,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Alert,
  Modal,
  TextInput,
  useWindowDimensions,
  type LayoutChangeEvent,
} from "react-native";
import { Trans, useTranslation } from 'react-i18next';
import {
  useSessionStore,
  getPersistedBillPayStepsMode,
} from "../../../../../../state/src/useSessionStore";
import type { SessionState, BillPayStepsMode } from "../../../../../../state/src/useSessionStore";
import { useApiWithLoading } from '../../../../hooks/useApiWithLoading';
import { useVisualViewportHeight } from '../../../../hooks/useVisualViewportHeight';
import { buildBillPayValidationPayload, callValidateBillPay } from "../../../../helper/billPayValidationHelper";
import {
  getBillPayPricingFingerprint,
  invalidateBillPayAdditionalInformationOnPricingChange,
  type BillPayPricingFingerprint,
} from "../../../../helper/billPayAdditionalInformationHelper";
import {
  buildBillPayFeeLookupPayload,
  callPostBillPayFeeLookup,
  buildBillPayGffpPayload,
  callGetBillPayFieldsForProduct,
  extractBillPayFeeInfo,
  syncBillPaySendOptionFromFeeLookup,
} from "../../../../helper/billPayFeelookupHelper";
// import { useGlobalError } from '../../../uiComponents/GlobalErrorProvider';
import { useGlobalError } from '../../../uiComponents/GlobalErrorProvider';
import { ComponentStyles } from "../../../../constants/componentStyles";

// Icons
import BackIcon from "../../../../../../../assets/images/BackIcon.svg";
import HelpIcon from "../../../../../../../assets/images/HelpIcon.svg";
import CloseIcon from "../../../../../../../assets/images/CloseIcon.svg";
import WarningBlackIcon from "../../../../../../../assets/images/WarningBlackIcon.svg";
import AccountLockedIcon from "../../../../../../../assets/images/AccountLocked.svg";
import { updateConsumerProfile, postAuthConsumer } from "../../../../services/send.service";
import { withKioskLogContext } from "../../../../helper/kioskLogContext";

// Bill Pay flow step components
// import EnterPhoneStep, { EnterPhoneStepHandle } from "./steps/EnterPhoneStep";
import EnterPhoneStep, { EnterPhoneStepHandle } from "../../common/steps/EnterPhoneStep";
import BillPayDOBStep, { BillPayDOBStepHandle } from "./steps/BillPayDOBStep";
import BillPayAddressStep, { BillPayAddressStepHandle } from "./steps/BillPayAddressStep";
import BillPayEnterNameStep, { BillPayEnterNameStepHandle } from "./steps/BillPayEnterNameStep";
import AccountInformationStep, { AccountInformationStepHandle } from "./steps/AccountInformationStep";
import EnterPinStep from "./steps/EnterPinStep";
import ConfirmationStep from "../../common/steps/ConfirmationStep";
import PayAmountStep from "./steps/PayAmountStep";
import BillPayReviewAndEditStep from "./steps/BillPayReviewAndEditStep";
import FraudPreventionStep from "../../common/steps/FraudPreventionStep";
import BillPayFinalReviewStep from "./steps/BillPayFinalReviewStep";
import BillPayHeadToCashierStep from "./steps/BillPayHeadToCashierStep";

// New Consumer step components
import EnterZipCodeStep from "./steps/EnterZipCodeStep";
import BillPayConfirmPinStep from "./steps/BillPayConfirmPinStep";

import { GffpHelper } from "../../../../helper/gffpHelper";
import { FieldsForProductInfo } from "../../../../view-models/FieldsForProductInfo";
import { logUpdateConsumerProfileDraft } from "../../../../helper/createProfileHelper";
import { SendMoneyPostUpdateConsumerProfileParameter } from "../../../../view-models/SendMoneyPostUpdateConsumerProfileParameter";
import { buildAuthConsumerPostParameter } from "../../../../helper/authConsumerHelper";
import { AuthConsumerInfo } from "../../../../view-models/AuthConsumerInfo";
import { RequiredFields } from "../../../../view-models/RequiredFields";
import PreviosBillerInfoStep from "./steps/PreviosBillerInfoStep";
import { FeeLookupInfo } from "../../../../view-models/feeInfo";
import AdditionalInformationStep, {
  type AdditionalInformationStepHandle,
} from "./steps/AdditionalInformationStep";
import { ConsumerRegistration } from "../../../../view-models/ConsumerRegistration";
import  Colors  from "../../../../constants/colors";
import NeedHelpComponent from "../../common/NeedHelpComponent";
import CalenderContainer from "../../../../../../../assets/images/CalendarContainer-2.svg";
import GradientBackground from "../../../uiComponents/GradientBackground";
import GradientExpandSwipeArea from "../../../uiComponents/GradientExpandSwipeArea";
import { FeeLookupHelper } from "../../../../helper/feeLookupHelper";
import { capturePosthogEvent, trackUIError } from '../../../../utils/analytics';
import type { WebFlowRoutingProps } from "../../../../navigation/webFlowRouting";
import { useWebFlowStepSync } from "../../../../hooks/useWebFlowStepSync";

interface MainSendScreenProps {
  handleClose?: () => void;
  handleFinish?: () => void;
  webFlowRouting?: WebFlowRoutingProps;
}

const EXISTING_CUNSUMER_STEPS = [
  { key: "phone", title: "Enter phone number", component: EnterPhoneStep },
  { key: "pin", title: "Enter your PIN", component: EnterPinStep },
  { key: "previosBillerInfo", title: "Your Address", component: PreviosBillerInfoStep },
  { key: "accountInformation", title: "Choose State of ID", component: AccountInformationStep },
  { key: "payAmount", title: "Pay Amount", component: PayAmountStep },
  { key: "reviewAndEdit", title: "Review", component: BillPayReviewAndEditStep },
  { key: "billPayDOB", title: "Your Date of Birth", component: BillPayDOBStep },
  { key: "billPayAddress", title: "Your Address", component: BillPayAddressStep },
  { key: "additionalInformation", title: "Additional Information", component: AdditionalInformationStep },
  { key: "fraudPrevention", title: "Fraude Prevention", component: FraudPreventionStep },
  { key: "billPayFinalReview", title: "Review", component: BillPayFinalReviewStep },
  { key: "billPayHeadToCashier", title: "Your identification number", component: BillPayHeadToCashierStep }
];

const NEW_CONSUMER_STEPS = [
  { key: "phone", title: "Enter phone number", component: EnterPhoneStep },
  { key: "previosBillerInfo", title: "Your Address", component: PreviosBillerInfoStep },
  { key: "accountInformation", title: "Choose State of ID", component: AccountInformationStep },
  { key: "payAmount", title: "Pay Amount", component: PayAmountStep },
  { key: "reviewAndEdit", title: "Review", component: BillPayReviewAndEditStep },
  { key: "billPayDOB", title: "Your Date of Birth", component: BillPayDOBStep },
  // { key: "billPayZipCode", title: "Your zip code", component: EnterZipCodeStep },
  { key: "billPayAddress", title: "Your Address", component: BillPayAddressStep },
  { key: "billPayFullName", title: "Your Name", component: BillPayEnterNameStep },
  { key: "fraudPrevention", title: "Fraude Prevention", component: FraudPreventionStep },
  { key: "billPayFinalReview", title: "Review", component: BillPayFinalReviewStep },
  { key: "additionalInformation", title: "Additional Information", component: AdditionalInformationStep },
  { key: "pin", title: "Enter your PIN", component: EnterPinStep },
  { key: 'confirmPin', title: "Confirm you PIN", component: BillPayConfirmPinStep},
  { key: 'pinCreated', title: "PIN Created", component: ConfirmationStep},
  { key: "billPayHeadToCashier", title: "Your identification number", component: BillPayHeadToCashierStep }
]

/** Final-review edits that re-run pricing/review and the new vs existing consumer tail. */
const BILL_PAY_FINAL_REVIEW_REFLOW_EDIT_KEYS = new Set([
  "payAmount",
  "previosBillerInfo",
]);

export const MainBillPayScreen: React.FC<MainSendScreenProps> = ({
  handleClose,
  handleFinish,
  webFlowRouting,
}) => {
  const { t } = useTranslation();
  const { executeWithLoading } = useApiWithLoading();
  const { showError } = useGlobalError();
  const [steps, setSteps] = useState(() => {
    const mode = getPersistedBillPayStepsMode();
    return mode === "new" ? NEW_CONSUMER_STEPS : EXISTING_CUNSUMER_STEPS;
  });
  const [currentStep, setCurrentStep] = useState(0);

  const billPayStepsMode = useSessionStore(
    (s: SessionState) => (s as SessionState).billPayStepsMode ?? "existing"
  );
  const setBillPayStepsMode = useSessionStore(
    (s: SessionState) => (s as SessionState).setBillPayStepsMode
  );

  useEffect(() => {
    const mode: BillPayStepsMode = billPayStepsMode ?? "existing";
    setSteps(mode === "new" ? NEW_CONSUMER_STEPS : EXISTING_CUNSUMER_STEPS);
  }, [billPayStepsMode]);

  useWebFlowStepSync(webFlowRouting, steps, currentStep, setCurrentStep);

  useEffect(() => {
    if (steps.length > 0 && currentStep >= steps.length) {
      setCurrentStep(Math.max(0, steps.length - 1));
    }
  }, [currentStep, steps.length]);

  const [returnToStep, setReturnToStep] = useState<string | null>(null);
  const [isStepValid, setIsStepValid] = useState(false);
  const [needHelp, setNeedHelp] = useState(false);
  const [isDOBConfirm, setIsDOBCOnfirm] = useState(false);
  const [isGradientExpand, setIsGradientExpand] = useState(false);
  const billPayReviewGradientStepKeys = useMemo(
    () => new Set<string>(["reviewAndEdit", "billPayFinalReview"]),
    []
  );
  /** Steps that render their own Continue (or gradient CTA); omit pinned shell footer — same idea as send `receiverName`. */
  const billPayStepsWithoutFooter = useMemo(
    () =>
      new Set<string>([
        "previosBillerInfo",
        "payAmount",
        "reviewAndEdit",
        "billPayFinalReview",
        "billPayAddress",
        "billPayFullName",
      ]),
    []
  );
  const hasFooterForCurrentBillPayStep = !billPayStepsWithoutFooter.has(
    steps[currentStep]?.key ?? ""
  );
  const billPayStickyKeyboardContinueKeys = useMemo(
    () => new Set<string>(["phone", "billPayDOB", "accountInformation"]),
    []
  );
  const billPayCurrentStepKey = steps[currentStep]?.key;
  const stickBillPayFooterAboveKeyboard = billPayStickyKeyboardContinueKeys.has(
    billPayCurrentStepKey ?? ""
  );
  useEffect(() => {
    if (billPayCurrentStepKey && billPayReviewGradientStepKeys.has(billPayCurrentStepKey)) {
      setIsGradientExpand(true);
    } else {
      setIsGradientExpand(false);
    }
  }, [billPayCurrentStepKey, billPayReviewGradientStepKeys]);

  const pendingSendOption = useSessionStore((s: SessionState) => (s as any).pendingSendOption);
  const currentSendAmount = useSessionStore((s: SessionState) => (s as any).currentSendAmount);
  const billPayBillerSearchResponseStore = useSessionStore((s: SessionState) => (s as any).billPayBillerSearchResponseStore);
  const billPayBillerAccountNumberStore = useSessionStore((s: SessionState) => (s as any).billPayBillerAccountNumberStore);
  const setBillPayFeeLookupResponseStore = useSessionStore((s: SessionState) => (s as any).setBillPayFeeLookupResponseStore);
  const setBillPayGFFPResponseStore = useSessionStore((s: SessionState) => (s as any).setBillPayGFFPResponseStore);
  const billPayFeeLookupResponseStore = useSessionStore((s: SessionState) => (s as any).billPayFeeLookupResponseStore);
  const setBillPayAllRequiredFieldsStore = useSessionStore((s: SessionState) => (s as any).setBillPayAllRequiredFieldsStore);
  const billPayAllRequiredFieldsStore = useSessionStore((s: SessionState) => (s as any).billPayAllRequiredFieldsStore);
  const agentId = useSessionStore((s: SessionState) => s.agentId);
  const unitProfileId = useSessionStore((s: SessionState) => s.unitProfileId);
  const setMissingRequiredFields = useSessionStore((s: SessionState) => (s as any).setMissingRequiredFields);
  const recordStepTransition = useSessionStore((s: SessionState) => (s as any).recordStepTransition);
  const isNewConsumer = useSessionStore((s: SessionState) => (s as any).isNewConsumer);
  const setIsNewConsumer = useSessionStore((s: SessionState) => (s as any).setIsNewConsumer);
  // Sender profile setters to hydrate from AuthConsumer
  const setConsumerId = useSessionStore((s: SessionState) => (s as any).setConsumerId);
  const setSenderFirstName = useSessionStore((s: SessionState) => (s as any).setSenderFirstName);
  const setSenderMiddleName = useSessionStore((s: SessionState) => (s as any).setSenderMiddleName);
  const setSenderLastName = useSessionStore((s: SessionState) => (s as any).setSenderLastName);
  const setSenderAddress = useSessionStore((s: SessionState) => (s as any).setSenderAddress);
  const setSenderCity = useSessionStore((s: SessionState) => (s as any).setSenderCity);
  const setSenderState = useSessionStore((s: SessionState) => (s as any).setSenderState);
  const setSenderCountry = useSessionStore((s: SessionState) => (s as any).setSenderCountry);
  const setSenderZipCode = useSessionStore((s: SessionState) => (s as any).setSenderZipCode);
  const setSenderDOBString = useSessionStore((s: SessionState) => (s as any).setSenderDOBString);
  const setSenderDOB = useSessionStore((s: SessionState) => (s as any).setSenderDOB);
  const setPhoneNumber = useSessionStore((s: SessionState) => (s as any).setPhoneNumber);
  const setConfirmationNumber = useSessionStore((s: SessionState) => (s as any).setConfirmationNumber);
  const setBillPayGetAuthConsumerResponseStore = useSessionStore((s: SessionState) => (s as any).setBillPayGetAuthConsumerResponseStore);
  const setBillPayValidationResponseStore = useSessionStore((s: SessionState) => (s as any).setBillPayValidationResponseStore);

  /** Forward path skipped Additional Information before Fraud. Mirrors Back from fraud. */
  const skippedAdditionalInformationBeforeFraudRef = useRef(false);
  /** Where Additional Information was opened from (for correct Back). */
  const additionalInformationNavSourceRef = useRef<
    "billPayDOB" | "billPayAddress" | "billPayFullName" | "billPayFinalReview" | null
  >(null);
  /** Set when Amount or Destination is edited from billPayFinalReview; cleared on re-entry to final review. */
  const billPayFinalReviewReflowRef = useRef(false);
  /** Last amount + destination used for additional-information collection (cleared when either changes). */
  const billPayAdditionalPricingContextRef = useRef<BillPayPricingFingerprint | null>(
    null,
  );

  const consumerIdFromStore = useSessionStore((s: SessionState) => (s as any).consumerId);
  const billPayGetAuthConsumerResponseStore = useSessionStore((s: SessionState) => (s as any).billPayGetAuthConsumerResponseStore);
  const senderFirstNameFromStore = useSessionStore((s: SessionState) => (s as any).senderFirstName);
  const senderMiddleNameFromStore = useSessionStore((s: SessionState) => (s as any).senderMiddleName);
  const senderLastNameFromStore = useSessionStore((s: SessionState) => (s as any).senderLastName);
  const senderAddressFromStore = useSessionStore((s: SessionState) => (s as any).senderAddress);
  const senderCityFromStore = useSessionStore((s: SessionState) => (s as any).senderCity);
  const senderStateFromStore = useSessionStore((s: SessionState) => (s as any).senderState);
  const senderCountryFromStore = useSessionStore((s: SessionState) => (s as any).senderCountry);
  const senderZipCodeFromStore = useSessionStore((s: SessionState) => (s as any).senderZipCode);
  const senderDOBStringFromStore = useSessionStore((s: SessionState) => (s as any).senderDOBString);
  const senderDOBFromStore = useSessionStore((s: SessionState) => (s as any).senderDOB);
  const phoneNumberFromStore = useSessionStore((s: SessionState) => (s as any).phoneNumber);
  const consumerPin = useSessionStore((s: SessionState) => (s as any).consumerPin);
  const setMgiTransactionSessionId = useSessionStore((s: SessionState) => (s as any).setMgiTransactionSessionId);
  const StepComponent = steps[currentStep]?.component;
  const AnyStepComponent = StepComponent as any;
  const [showRefResultModal, setShowRefResultModal] = useState(false);
  const [refResult, setRefResult] = useState<{ receiverName?: string; senderName?: string; receiveAmount?: string | number; referenceNumber?: string | number } | null>(null);
  
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  const [resetSignal, setResetSignal] = useState(0);
  const [pinStepApiError, setPinStepApiError] = useState<string | null>(null);
  const pinErrorResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setConsumerPin = useSessionStore((s: any) => s.setConsumerPin);
  const inputs = useRef<(TextInput | null)[]>([]);
  const [locked, setLocked] = useState(false);
  const [showPinAttemptsWarning, setShowPinAttemptsWarning] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const selectedBillerInformation = useSessionStore((s: SessionState) => (s as any).selectedBillerInformationStore);
  const PhoneStepRef = React.useRef<EnterPhoneStepHandle | null>(null);
  const BillPayDOBStepRef = React.useRef<BillPayDOBStepHandle | null>(null);
  const BillPayAddressStepRef = React.useRef<BillPayAddressStepHandle | null>(null);
  const BillPayEnterNameStepRef = React.useRef<BillPayEnterNameStepHandle | null>(null);
  const AdditionalInformationStepRef =
    React.useRef<AdditionalInformationStepHandle | null>(null);
  const AccountInformationStepRef = React.useRef<AccountInformationStepHandle | null>(null);
  const selectedSendOption = useSessionStore((s: SessionState) => (s as any).selectedSendOption);
  const setSelectedSendOption = useSessionStore((s: SessionState) => (s as any).setSelectedSendOption);
  const setPendingSendOption = useSessionStore((s: SessionState) => (s as any).setPendingSendOption);
  const sendAmount = useSessionStore((s: SessionState) => s.currentSendAmount);
  const storedBillerAccountNumber = useSessionStore((s: SessionState) => (s as any).billPayBillerAccountNumberStore);

  const scrollRef = useRef<ScrollView>(null);
  const { height: screenHeight } = useWindowDimensions();
  const webVisualViewportHeight = useVisualViewportHeight();
  const layoutHeight =
    Platform.OS === "web" ? webVisualViewportHeight ?? screenHeight : screenHeight;
  /** Web: always follow visual viewport height so the shell shrinks with the iOS keyboard (no dead band under Continue). */
  // const effectiveLayoutHeight = layoutHeight;
  /**
   * Web (iOS Safari): single-field steps shrink with `visualViewport` so Continue sits above the keyboard.
   * Multi-field steps keep full window height so the footer stays at the bottom (scroll the form; keyboard may cover the lower area).
   */
  const effectiveLayoutHeight =
    Platform.OS === "web"
      ? stickBillPayFooterAboveKeyboard
        ? layoutHeight
        : screenHeight
      : layoutHeight;
  const [headerHeight, setHeaderHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);

  const scrollAreaHeight =
    effectiveLayoutHeight > 0
      ? effectiveLayoutHeight -
        headerHeight -
        (hasFooterForCurrentBillPayStep && footerHeight > 0 ? footerHeight : 0)
      : undefined;
  /** Web: track visual viewport for sticky-keyboard steps and the biller list (not full window height). */
  const billPayUseVisualViewportShellHeight =
    Platform.OS === "web" &&
    (stickBillPayFooterAboveKeyboard || billPayCurrentStepKey === "previosBillerInfo");
  const containerHeight =
    effectiveLayoutHeight > 0
      ? Platform.OS === "web" && billPayUseVisualViewportShellHeight
        ? layoutHeight
        : effectiveLayoutHeight
      : undefined;
  const showHeaderSpacer = Platform.OS === "web" && headerHeight > 0;
  const isBillPayReviewOrFinalReviewScroll =
    billPayCurrentStepKey === "reviewAndEdit" ||
    billPayCurrentStepKey === "billPayFinalReview";

  useEffect(() => {
    // scroll to top on step change
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ y: 0, animated: false });
    });
  }, [currentStep]);

  useEffect(() => {
    if (steps[currentStep]?.key === "billPayFinalReview") {
      billPayFinalReviewReflowRef.current = false;
    }
  }, [currentStep, steps]);

  const billPayFeeInfoFromStore = useMemo(
    () => extractBillPayFeeInfo(billPayFeeLookupResponseStore),
    [billPayFeeLookupResponseStore],
  );
  const option: any =
    billPayFeeInfoFromStore || pendingSendOption || selectedSendOption || null;
  
    const summary = useMemo(() => {
      if (!option) {
        return {
          sendAmount: 0,
          sendCurrency: 'USD',
          receiveAmount: 0,
          receiveCurrency: '—',
          exchangeRate: 0,
          fees: 0,
          total: 0,
        };
      }
      const sendCurrency: string = option?.SendAmounts?.SendCurrency || 'USD';
      const sendAmount: number = Number(option?.SendAmounts?.SendAmount || 0);
      const receiveCurrency: string = option?.ReceiveAmounts?.ValidCurrencyIndicator
        ? (option?.ValidReceiveCurrency || '')
        : (option?.EstimatedReceiveCurrency || option?.ValidReceiveCurrency || '');
      const receiveAmount: number = Number(option?.ValidReceiveAmount ?? option?.EstimatedReceiveAmount ?? 0);
      const exchangeRateNum: number = Number(FeeLookupHelper.getExchangeRate(option) || 0);
      const fees: number = Number(option?.SendAmounts?.TotalDiscountAmount || 0) + Number(option?.SendAmounts?.TotalSendFees || 0);
      const totalBase: number = Number(option?.TotalAmount || 0);
      const total: number = totalBase;
      return { sendAmount, sendCurrency, receiveAmount, receiveCurrency, exchangeRate: exchangeRateNum, fees, total };
    }, [billPayFeeInfoFromStore, pendingSendOption, selectedSendOption]);
  
    const totalsFromFeeLookup = useMemo(() => {
      try {
        const raw: any = billPayFeeLookupResponseStore as any;
  
         // Normalize inputs that may be JSON strings
         const normalizedFeeLookup: any = (() => {
          let raw: any = billPayFeeLookupResponseStore as any;
          if (typeof raw === 'string') {
            try { raw = JSON.parse(raw); } catch {}
          }
          return raw;
        })();
  
        const totalFeesRaw: any = normalizedFeeLookup?.FeeInfos[0].SendAmounts.TotalSendFees
        ?? 0;
        const totalAmountRaw: any = normalizedFeeLookup?.FeeInfos[0].TotalAmount?? 0;
        const parseNum = (v: any) => {
          const s = String(v ?? '0');
          const n = Number(s.replace(/,/g, ''));
          return isFinite(n) ? n : 0;
        };
        return {
          totalFees: parseNum(totalFeesRaw),
          totalAmount: parseNum(totalAmountRaw),
        };
      } catch {
        return { totalFees: 0, totalAmount: 0 };
      }
    }, [billPayFeeLookupResponseStore]);

  // initialize timing for first step
  useEffect(() => {
    try { recordStepTransition?.(steps[0].key); } catch { }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Callback each step can use to report validity
  const handleValidationChange = (valid: boolean) => {
    setIsStepValid(valid);
  };

  const handleEditStep = (key: string) => {
    const targetIndex = steps.findIndex((s) => s.key === key);
    if (targetIndex === -1) return;
    if (key === "additionalInformation") {
      try {
        syncBillPayMissingRequiredFieldsIntoStore();
      } catch {}
      additionalInformationNavSourceRef.current = "billPayFinalReview";
    }
    const fromKey = steps[currentStep]?.key;
    if (
      fromKey === "billPayFinalReview" &&
      BILL_PAY_FINAL_REVIEW_REFLOW_EDIT_KEYS.has(key)
    ) {
      billPayFinalReviewReflowRef.current = true;
      setReturnToStep(null);
    } else {
      billPayFinalReviewReflowRef.current = false;
      setReturnToStep(fromKey ?? null);
    }
    setCurrentStep(targetIndex);
  };

  // const handleForgotPassword = () => {
  //   try { setIsNewConsumer?.(true); } catch {}
  //   setLocked(false);
  //   const targetIndex = steps.findIndex(s => s.key === "previosBillerInfo");
  //   if (targetIndex !== -1) {
  //     setCurrentStep(targetIndex);
  //   }
  // };
  const handleForgotPassword = () => {
    // mark as new consumer so finalReview routes to PIN
    setIsNewConsumer?.(true);
    // Close pop up
    setShowPopup(false);
    setLocked(false);

    //find index from the NEW flow, not the current one
    const newFlow = NEW_CONSUMER_STEPS;
    const countryIndex = newFlow.findIndex(s => s.key === 'previosBillerInfo');

    // update both pieces of state together
    setSteps(newFlow);
    setBillPayStepsMode?.("new");
    if (countryIndex !== -1) {
      setCurrentStep(countryIndex);
    }
  }

  const handleResetPin = () => {
    // Bill Pay does not have a dedicated OTP reset flow yet.
    // Route through guest/new-consumer path so the user can continue.
    setShowPopup(false);
    setLocked(false);
    handleForgotPassword();
  }

  const handleBillPayDOBNavigation = () => {
     if(!isNewConsumer){
          try {
          const missing = computeBillPayMissingRequiredFields();
          if (missing && missing.length > 0) {
            try { setMissingRequiredFields?.(missing as any); } catch {}
            skippedAdditionalInformationBeforeFraudRef.current = false;
            additionalInformationNavSourceRef.current = "billPayDOB";
            const aiIndex = steps.findIndex(s => s.key === "additionalInformation");
            if (aiIndex !== -1) {
              setCurrentStep(aiIndex);
              return;
            }
          }
        } catch { }
        }
        captureBillPayDOBConfirmEvent(senderDOBStringFromStore)
        const targetKey = "billPayAddress";
        const targetIndex = steps.findIndex(s => s.key === targetKey);
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
  }

  const captureBillPayPhoneNumberEvent = (billpayPhoneNumber: string) => {
    capturePosthogEvent('Bill Pay Flow - Phone Number Capture Step', {
      'data-mg-billpay-phone-number': billpayPhoneNumber
    });
  }
  const captureBillPayPreviousBillerEvent = (billerName: string) => {
    capturePosthogEvent('Bill Pay Flow - Previous Biller Information Capture Step', {
      'data-mg-billpay-previous-biller-name': billerName
    });
  }
  
  const captureBillPayAccountNumberEvent = (billerNotes: string, storedBillerAccountNumber: string) => {
    capturePosthogEvent('Bill Pay Flow - Account Information Capture Step', {
      'data-mg-billpay-biller-notes': billerNotes,
      'data-mg-billpay-account-number': storedBillerAccountNumber
    });
  }
  const captureBillPayAmountEvent = (currentSendAmount: string) => {
    capturePosthogEvent('Bill Pay Flow - Amount Capture Step', {
      'data-mg-billpay-amount': currentSendAmount
    });
  }
  const captureReviewAndEditEvent = () => {
    capturePosthogEvent('Bill Pay Flow - Review And Edit Step', {
      'data-mg-billapy-review-and-Edit': 'true'
    });
  }
  const captureBillPayDOBEvent = (billerDOB: string) => {
    capturePosthogEvent('Bill Pay Flow - DOB Capture Step', {
      'data-mg-billpay-dob': billerDOB
    });
  }
  const captureBillPayDOBConfirmEvent = (billerDOB: string) => {
    capturePosthogEvent('Bill Pay Flow - DOB Confirm Capture Step', {
      'data-mg-billpay-dob-confirm': billerDOB
    });
  }
  const captureBillPayAddressEvent = (senderAddressFromStore: string, senderCityFromStore: string, senderZipCodeFromStore: string, senderStateFromStore: string) => {
    capturePosthogEvent('Bill Pay Flow - Address Capture Step', {
      'data-mg-billpay-address': senderAddressFromStore,
      'data-mg-billpay-city': senderCityFromStore,
      'data-mg-billpay-zipcode': senderZipCodeFromStore,
      'data-mg-billpay-state': senderStateFromStore,
    });
  }
  const captureBillPayFullnameEvent = (senderFirstName: string, senderMiddleName: string, senderLastName: string) => {
    capturePosthogEvent('Bill Pay Flow - Full Name Capture Step', {
      'data-mg-billpay-firstName': senderFirstName,
      'data-mg-billpay-middleName': senderMiddleName,
      'data-mg-billpay-lastName': senderLastName,
    });
  }
  const captureFinalReviewEvent = () => {
    capturePosthogEvent('Bill Pay Flow - Final Review Step', {
      'data-mg-billapy-final-review': 'true'
    });
  }
  const captureBillPayPinEvent = (consumerPin: string) => {
    capturePosthogEvent('Bill Pay Flow - PIN Capture Step', {
      'data-mg-billpay-pin': consumerPin
    });
  }
  const captureBillpayConfirmPinEvent = (consumerPin: string) => {
      capturePosthogEvent('Bill Pay Flow - Confirm PIN Capture Step', {
        'data-mg-billpay-confirm-pin': consumerPin
      });
    }
  const captureBillpayPINCreatedEvent = () => {
    capturePosthogEvent('Bill Pay Flow - PIN Created Step', {
      'data-mg-billpay-pin-created': 'true'
    });
  }
  const captureFraudPreventionEvent = () => {
    capturePosthogEvent('Bill Pay Flow - Fraud Prevention Step', {
      'data-mg-billpay-fraud-prevention': 'true'
    });
  }
  const captureHeadToCashierEvent = () => {
      capturePosthogEvent('Bill Pay Flow - Head to Cashier Step', {
        'data-mg-billpay-head-to-cashier': 'true'
      });
    }

  const handleNext = async (countryCode?: string) => {
    await executeWithLoading(async () => {
      // Return to previous step while edit flow (not from additionalInformation or payAmount — validate / FeeLookup first)
      if (
        returnToStep &&
        steps[currentStep].key !== "additionalInformation" &&
        steps[currentStep].key !== "payAmount"
      ) {
        const targetIndex = steps.findIndex(s => s.key === returnToStep);
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
        }
        setReturnToStep(null);
        return;
      }
      if (steps[currentStep].key === "phone") {
        try {
          const isValid = await PhoneStepRef.current?.phoneSubmit();
          if (!isValid) return;
          const result = await PhoneStepRef.current?.submit();
          captureBillPayPhoneNumberEvent(phoneNumberFromStore);
          if (result === "notfound" || result === "externalSenderProfile") {
            setSteps(() => NEW_CONSUMER_STEPS);
            setBillPayStepsMode?.("new");
            const billerIdx = NEW_CONSUMER_STEPS.findIndex(
              (s) => s.key === "previosBillerInfo"
            );
            requestAnimationFrame(() => {
              if (billerIdx !== -1) setCurrentStep(billerIdx);
            });
            return;
          }
          setSteps(EXISTING_CUNSUMER_STEPS);
          setBillPayStepsMode?.("existing");
          const pinIdx = EXISTING_CUNSUMER_STEPS.findIndex((s) => s.key === "pin");
          setCurrentStep(pinIdx !== -1 ? pinIdx : 1);
          return;
        } catch (error: any) {
          try {
            showError(error?.message || t("PHONE_STEP_ERROR_FAILED"));
          } catch {}
          return;
        }
      } else if (steps[currentStep].key === "pin") {
         // If this is a new consumer, go to confirm PIN step instead of auth
        if (isNewConsumer) {
          captureBillPayPinEvent(consumerPin)
          const confirmIndex = steps.findIndex(s => s.key === "confirmPin");
          if (confirmIndex !== -1) {
            try { recordStepTransition?.("confirmPin"); } catch {}
            setCurrentStep(confirmIndex);
            return;
          }
        } 
           await handlePinVerification();
          //TODO: we need to load previosBillerInfo only when billerInfo found in getAuthConsumerResponse
          // const targetIndex = steps.findIndex(s => s.key === "previosBillerInfo");
          // if (targetIndex !== -1) {
          //   setCurrentStep(targetIndex);
          //   return;
          // }
      }  else if (steps[currentStep].key === "confirmPin") {
        // After confirming PIN for a new consumer, go to PIN created or head to cashier path
        // No early redirect to additionalInformation here; we will handle missing fields after receiverName
        const result = await handleBillPayFinalReview();
        // Stay on current step If any error
        if (!result) {
          // An error was shown; stay on current step
          return;
        }
        captureBillpayConfirmPinEvent(consumerPin)
        if(isNewConsumer) {
          const targetIndex = steps.findIndex(s => s.key === "pinCreated");
          if (targetIndex !== -1) {
            try { recordStepTransition?.("pinCreated"); } catch {}
            setCurrentStep(targetIndex);
            return;
          }
        }
        const targetIndex = steps.findIndex(s => s.key === "billPayHeadToCashier");
        if (targetIndex !== -1) {
          try { recordStepTransition?.("billPayHeadToCashier"); } catch {}
          setCurrentStep(targetIndex);
          return;
        }
      } 
      else if (steps[currentStep].key === "previosBillerInfo") {
        const billerName = selectedBillerInformation?.BillerName;
        captureBillPayPreviousBillerEvent(billerName);
        if (applyBillPayPricingContextUpdate()) {
          try { setMissingRequiredFields?.([]); } catch {}
        }
        const targetIndex = steps.findIndex(s => s.key === "accountInformation");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "accountInformation") {
        const isValid = await AccountInformationStepRef.current?.accountNumberSubmit();
        if (!isValid) return;
        const billerNotes = selectedBillerInformation?.BillerNotes
        captureBillPayAccountNumberEvent(billerNotes, storedBillerAccountNumber)
        const targetIndex = steps.findIndex(s => s.key === "payAmount");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "payAmount") {
        if (!isStepValid){
          showError(t("ERROR_PLEASE_ENTER_A_VALID_AMOUNT"));
          return
        }
        const ok = await handlePayAmount();
        if (!ok) {
          return; // stay on this step when fee lookup/gffp fails
        }
        const reflowingFromFinalReview = billPayFinalReviewReflowRef.current;
        const targetKey = reflowingFromFinalReview
          ? "reviewAndEdit"
          : returnToStep || "reviewAndEdit";
        const targetIndex = steps.findIndex((s) => s.key === targetKey);
        if (targetIndex !== -1) {
          if (!reflowingFromFinalReview && returnToStep) {
            try { recordStepTransition?.(returnToStep); } catch {}
          } else if (reflowingFromFinalReview) {
            try { recordStepTransition?.("reviewAndEdit"); } catch {}
          }
          setReturnToStep(null);
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "reviewAndEdit") {
        captureReviewAndEditEvent()
        setIsGradientExpand(false)
        const targetIndex = steps.findIndex(s => s.key === "billPayDOB");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "billPayDOB") {
        const isValid = await BillPayDOBStepRef.current?.submit();
        if (!isValid) return; // stay on same step
        captureBillPayDOBEvent(senderDOBStringFromStore)
        setIsDOBCOnfirm(true);
        // After DOB, ensure all required fields are present; if missing, branch to AdditionalInformation
        // if(!isNewConsumer){
        //   try {
        //   const missing = computeBillPayMissingRequiredFields();
        //   if (missing && missing.length > 0) {
        //     try { setMissingRequiredFields?.(missing as any); } catch {}
        //     const aiIndex = steps.findIndex(s => s.key === "additionalInformation");
        //     if (aiIndex !== -1) {
        //       setCurrentStep(aiIndex);
        //       return;
        //     }
        //   }
        // } catch { }
        // }
        // // Check consumer is new or existing and then move
        // const targetKey = isNewConsumer ? 'billPayAddress' : 'fraudPrevention';
        // const targetIndex = steps.findIndex(s => s.key === targetKey);
        // if (targetIndex !== -1) {
        //   setCurrentStep(targetIndex);
        //   return;
        // }
      } else if (steps[currentStep].key === "billPayAddress") {
        const isValid = await BillPayAddressStepRef.current?.submit();
        if (!isValid) return;
        captureBillPayAddressEvent(senderAddressFromStore, senderCityFromStore, senderZipCodeFromStore, senderStateFromStore)
        if (isNewConsumer) {
          const targetIndex = steps.findIndex(s => s.key === "billPayFullName");
          if (targetIndex !== -1) {
            setCurrentStep(targetIndex);
            return;
          }
        } else {
          try {
            const missing = computeBillPayMissingRequiredFields();
            if (missing && missing.length > 0) {
              try { setMissingRequiredFields?.(missing as any); } catch {}
              skippedAdditionalInformationBeforeFraudRef.current = false;
              additionalInformationNavSourceRef.current = "billPayAddress";
              setReturnToStep("fraudPrevention");
              const aiIndex = steps.findIndex(s => s.key === "additionalInformation");
              if (aiIndex !== -1) {
                setCurrentStep(aiIndex);
                return;
              }
            }
          } catch (e) {
            console.warn('Failed to compute missing fields after billPayAddress', e);
          }
          skippedAdditionalInformationBeforeFraudRef.current = true;
          const targetIndex = steps.findIndex(s => s.key === "fraudPrevention");
          if (targetIndex !== -1) {
            setCurrentStep(targetIndex);
            return;
          }
        }
      } else if (steps[currentStep].key === "billPayFullName") {
        const isValid = await BillPayEnterNameStepRef.current?.submit();
        if (!isValid) return;
        captureBillPayFullnameEvent(senderFirstNameFromStore, senderMiddleNameFromStore, senderLastNameFromStore)
         // After receiver name, check for any missing required fields from GFFP
         try {
          const missing = computeBillPayMissingRequiredFields();
          if (missing && missing.length > 0) {
            try { setMissingRequiredFields?.(missing as any); } catch {}
            skippedAdditionalInformationBeforeFraudRef.current = false;
            additionalInformationNavSourceRef.current = "billPayFullName";
            // Route to Additional Information, then return back to Fraud Prevention after completion
            setReturnToStep("fraudPrevention");
            const aiIndex = steps.findIndex(s => s.key === "additionalInformation");
            if (aiIndex !== -1) {
              setCurrentStep(aiIndex);
              return;
            }
          }
        } catch (e){
          console.warn('Failed to compute missing fields after receiverName', e);
        }
        // No missing fields → proceed to Fraud Prevention
        skippedAdditionalInformationBeforeFraudRef.current = true;
        const targetIndex = steps.findIndex(s => s.key === "fraudPrevention");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "additionalInformation") {
        const isValid = AdditionalInformationStepRef.current?.submit() ?? false;
        if (!isValid) {
          setIsStepValid(false);
          return;
        }
        if (returnToStep) {
          const targetIndex = steps.findIndex((s) => s.key === returnToStep);
          if (targetIndex !== -1) {
            try { recordStepTransition?.(returnToStep); } catch { }
            additionalInformationNavSourceRef.current = null;
            setCurrentStep(targetIndex);
            setReturnToStep(null);
            return;
          }
        }
        if (
          additionalInformationNavSourceRef.current === "billPayDOB" &&
          !isNewConsumer
        ) {
          const addressIdx = steps.findIndex((s) => s.key === "billPayAddress");
          if (addressIdx !== -1) {
            try { recordStepTransition?.("billPayAddress"); } catch {}
            additionalInformationNavSourceRef.current = null;
            setCurrentStep(addressIdx);
            return;
          }
        }
        const nextIdx = steps.findIndex(s => s.key === "fraudPrevention");
        if (nextIdx !== -1) {
          try { recordStepTransition?.("fraudPrevention"); } catch { }
          additionalInformationNavSourceRef.current = null;
          setCurrentStep(nextIdx);
          return;
        }
      } else if (steps[currentStep].key === "fraudPrevention") {
        captureFraudPreventionEvent()
        const targetIndex = steps.findIndex(s => s.key === "billPayFinalReview");
        if (targetIndex !== -1) {
          try { recordStepTransition?.("billPayFinalReview"); } catch { }
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "billPayFinalReview") {
        // const ok = await handleBillPayFinalReview();
        // if (!ok) {
        //   return; // stay on this step when validation fails
        // }
        // const targetIndex = steps.findIndex(s => s.key === "billPayHeadToCashier");
        // if (targetIndex !== -1) {
        //   try { recordStepTransition?.("billPayHeadToCashier"); } catch { }
        //   setCurrentStep(targetIndex);
        //   return;
        // }
                // If this is a new consumer, send them to the PIN step before proceeding
        if (isNewConsumer) {
          captureFinalReviewEvent()
          const pinIndex = steps.findIndex(s => s.key === "pin");
          if (pinIndex !== -1) {
            try { recordStepTransition?.("pin"); } catch { }
            setCurrentStep(pinIndex);
            return;
          }
        } else {
          // For existing consumers, perform update/validate/post flow here.
          // If validation reports missing fields, redirect to Additional Information.
          try {
            const result = await handleBillPayFinalReview();
            if (!result) {
              // An error was shown; stay on current step
              return;
            }
            captureFinalReviewEvent()
            if (result && (result as any).missingFieldsFound) {
              skippedAdditionalInformationBeforeFraudRef.current = false;
              additionalInformationNavSourceRef.current = "billPayFinalReview";
              const aiIndex = steps.findIndex(s => s.key === "additionalInformation");
              if (aiIndex !== -1) {
                setCurrentStep(aiIndex);
                return;
              }
            }
          } catch (e) {
            console.warn('maultipleCalls failed', e);
          }
        }
        captureFinalReviewEvent()
        const targetIndex = steps.findIndex(s => s.key === "billPayHeadToCashier");
        if (targetIndex !== -1) {
          try { recordStepTransition?.("billPayHeadToCashier"); } catch {}
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "pinCreated") {
        captureBillpayPINCreatedEvent()
        const targetIndex = steps.findIndex(s => s.key === "billPayHeadToCashier");
        if (targetIndex !== -1) {
          try { recordStepTransition?.("billPayHeadToCashier"); } catch {}
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "billPayHeadToCashier") {
        captureHeadToCashierEvent();
        try { handleFinish?.(); } catch {}
        // Build and log update consumer profile draft before proceeding
        const targetIndex = steps.findIndex(s => s.key === "billPayHeadToCashier");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      }
    }, t('STRKEY_PROCESSING'));
  };

  const handlePinVerification = async () => {
    // replace with real validation
    const handlePinVerificationFailure = () => {
      if (pinErrorResetTimeoutRef.current) {
        clearTimeout(pinErrorResetTimeoutRef.current);
        pinErrorResetTimeoutRef.current = null;
      }
      setPinStepApiError(t("ERROR_INCORRECT_PIN"));
      setShowPinAttemptsWarning(true);
      const remaining = attemptsLeft - 1;
      setAttemptsLeft(remaining);
      if (remaining <= 0) {
        setLocked(true);
        setShowPopup(true);
        setResetSignal(prev => prev + 1);
        setConsumerPin("");
        return;
      }
      pinErrorResetTimeoutRef.current = setTimeout(() => {
        setPinStepApiError(null);
        setResetSignal(prev => prev + 1);
        setConsumerPin("");
        pinErrorResetTimeoutRef.current = null;
      }, 350);
    };

    try {
      const authConsumerPostParameter = buildAuthConsumerPostParameter();
      const authConsumerResponse: AuthConsumerInfo = await postAuthConsumer(authConsumerPostParameter);
      const respAny: any = authConsumerResponse as any;
      if (respAny?.ErrorDetail || respAny?.errorDetail) {
        handlePinVerificationFailure();
        return; // stay on PIN step when auth returns an error detail
      }
      // Persist full response for reuse across steps
      try { setBillPayGetAuthConsumerResponseStore?.(respAny); } catch { }
      // Hydrate sender profile details into the store from successful AuthConsumer
      const ci: any = respAny?.ConsumerInfo ?? respAny?.consumerInfo;
      if (ci) {
        // Consumer ID
        try { setConsumerId?.(String(ci.ConsumerId ?? ci.consumerId ?? '')); } catch { }
        try { setSenderFirstName?.(ci.SenderFirstName ?? ci.senderFirstName ?? null); } catch { }
        try { setSenderMiddleName?.(ci.SenderMiddleName ?? ci.senderMiddleName ?? null); } catch { }
        try { setSenderLastName?.(ci.SenderLastName ?? ci.senderLastName ?? null); } catch { }
        try { setSenderAddress?.(ci.SenderAddress ?? ci.senderAddress ?? null); } catch { }
        try { setSenderCity?.(ci.SenderCity ?? ci.senderCity ?? null); } catch { }
        try { setSenderState?.(ci.SenderState ?? ci.senderState ?? null); } catch { }
        try {
          const countryRaw = ci.SenderCountry ?? ci.senderCountry ?? null;
          const countryCode =
            countryRaw != null && String(countryRaw).trim()
              ? String(countryRaw).trim().toUpperCase()
              : null;
          setSenderCountry?.(countryCode);
        } catch { }
        try { setSenderZipCode?.(ci.SenderZipCode ?? ci.senderZipCode ?? null); } catch { }
        // Phone
        try { setPhoneNumber?.(ci.SenderHomePhone ?? ci.senderHomePhone ?? null); } catch { }
        // DOB
        const dobRaw: any = ci.SenderDOB ?? ci.senderDOB ?? null;
        if (dobRaw) {
          const dobStr = String(dobRaw).split('T')[0]; // handle YYYY-MM-DD or YYYY-MM-DDThh:mm:ss
          try { setSenderDOBString?.(dobStr || null); } catch { }
          const dobDate = new Date(dobStr);
          if (!isNaN(dobDate as any)) {
            try { setSenderDOB?.(dobDate); } catch { }
          }
        }
      }
    } catch (e: any) {
      console.warn("BillPay PIN verification failed", e);
      handlePinVerificationFailure();
      return;
    }
    if (pinErrorResetTimeoutRef.current) {
      clearTimeout(pinErrorResetTimeoutRef.current);
      pinErrorResetTimeoutRef.current = null;
    }
    setPinStepApiError(null);
    setShowPinAttemptsWarning(false);
    captureBillPayPinEvent(consumerPin);
    const targetIndex = steps.findIndex(s => s.key === "previosBillerInfo");
    if (targetIndex !== -1) {
      setCurrentStep(targetIndex);
      return;
    }
  };

  useEffect(() => {
    return () => {
      if (pinErrorResetTimeoutRef.current) {
        clearTimeout(pinErrorResetTimeoutRef.current);
      }
    };
  }, []);

  const handlePayAmount = async (): Promise<boolean> => {
    try {
      const billerInfo: any = (billPayBillerSearchResponseStore?.BillerInfo ?? billPayBillerSearchResponseStore?.billerInfo ?? billPayBillerSearchResponseStore) as any;
      //CHECK: IAM ASSUMING THERE IS ONLY ONE BILLER INFO, SO USING 0 INDEX
      const receiveCode = billerInfo?.BillerInfos[0].ReceiveCode;
      const receiveAgentID = billerInfo?.BillerInfos[0].ReceiveAgentId;
      const amountNum = Number(String(currentSendAmount).replace(/,/g, ""));
      const payload = buildBillPayFeeLookupPayload({
        agentID: agentId,
        unitProfileID: unitProfileId,
        AmountItem: amountNum,
        amountExcludingFee: amountNum,
        receiveCode,
        receiveAgentID,
      });
      let feeResp: FeeLookupInfo;
      try {
        feeResp = await callPostBillPayFeeLookup(payload);
        const rawData: any = (feeResp && (feeResp as any).data !== undefined)
        ? (feeResp as any).data
        : feeResp;
        const feeLookupOk = handleError(rawData);
        if (!feeLookupOk) {
          return false;
        }
        try { setBillPayFeeLookupResponseStore?.(rawData); } catch { }
        syncBillPaySendOptionFromFeeLookup(rawData, {
          setSelectedSendOption,
          setPendingSendOption,
        });
        captureBillPayAmountEvent(currentSendAmount);
      } catch (e: any) {
        const msg = String(e?.message ?? t("ERROR_FEE_LOOKUP_FAILED"));
        try { showError(msg); } catch {}
        Alert.alert(t('ERROR') || t('Error'), msg, [{ text: t('STRKEY_OK') }]);
        return false; // stay on this step
      }
     
      // On successful fee lookup, fetch fields for product
      try {
        const gffpPayload = buildBillPayGffpPayload({
          agentID: agentId,
          unitProfileID: unitProfileId,
          receiveAgentID: receiveAgentID,
          billerAccountNumber: billPayBillerAccountNumberStore,
          amount: currentSendAmount,
          customerId: consumerIdFromStore || "0",
        });
        try {
          const gffpResp = await callGetBillPayFieldsForProduct(gffpPayload);
          const billPayAllRequiredFields: any[] = GffpHelper.filterRequiredAndOptionalFields(gffpResp) as any[];

          try { setBillPayGFFPResponseStore?.(gffpResp); } catch { }
          try { setBillPayAllRequiredFieldsStore?.(billPayAllRequiredFields); } catch { }
        } catch (e: any) {
          const msg = String(e?.message ?? t("ERROR_UNABLE_TO_LOAD_REQUIRED_FIELDS"));
          try { showError(msg); } catch {}
          Alert.alert(t('ERROR') || t('ERROR'), msg, [{ text: t('STRKEY_OK') }]);
          return false; // stay on this step
        }
      } catch (e) {
        // Defensive catch; error already handled above
      }
      applyBillPayPricingContextUpdate();
      syncBillPayMissingRequiredFieldsIntoStore();
    } catch (e: any) {
      const msg = String(e?.message ?? t("ERROR_AN_ERROR_OCCURED"));
      try { showError(msg); } catch {}
      Alert.alert(t('ERROR') || t('ERROR'), msg, [{ text: t('STRKEY_OK')  }]);
      return false; // stay on this step
    }
    return true;
  };

  const handleBillPayFinalReview = async (options?: { skipUpdateProfile?: boolean }): Promise<boolean> => {
    const skipUpdate = !!options?.skipUpdateProfile;
     // Ensure no required fields are missing before calling API; if missing, route to AdditionalInformation
     try {
      try {
        const missing = syncBillPayMissingRequiredFieldsIntoStore();
        if (missing && missing.length > 0) {
          skippedAdditionalInformationBeforeFraudRef.current = false;
          additionalInformationNavSourceRef.current = "billPayFinalReview";
          const aiIndex = steps.findIndex(s => s.key === "additionalInformation");
          if (aiIndex !== -1) {
            setCurrentStep(aiIndex);
          }
          return false;
        }
      } catch {}
      // handle skip for new consumer in pin screen
      let effectiveConsumerId: string | undefined = undefined;
      if (!skipUpdate) {
        const profile: SendMoneyPostUpdateConsumerProfileParameter = logUpdateConsumerProfileDraft();
        profile.unitProfileID = unitProfileId || "";
        profile.agentID = agentId || "";
        const profileResponse = ''; //await updateConsumerProfile(profile);
        try {
         effectiveConsumerId =  profileResponse; //(profileResponse && (profileResponse.consumerId || profileResponse.ConsumerId || profileResponse.id || profileResponse.Id)) ?? undefined;
        } catch {}
      } else {
        // For skip flow, attempt to use existing consumerId from store; fall back to "0"
        try { effectiveConsumerId = consumerIdFromStore ? String(consumerIdFromStore) : ''; } catch { effectiveConsumerId = "0"; }
      }
      // Build ValidateBillPay payload and call API
      const billerInfo: any = (billPayBillerSearchResponseStore?.BillerInfo ?? billPayBillerSearchResponseStore?.billerInfo ?? billPayBillerSearchResponseStore) as any;
      const receiveCode = billerInfo?.BillerInfos?.[0]?.ReceiveCode;
      const receiveAgentID = billerInfo?.BillerInfos?.[0]?.ReceiveAgentId;

      const amountNumber = Number(String(currentSendAmount || "0").replace(/,/g, ""));

      // Normalize inputs that may be JSON strings
      const normalizedFeeLookup: any = (() => {
        let raw: any = billPayFeeLookupResponseStore as any;
        if (typeof raw === 'string') {
          try { raw = JSON.parse(raw); } catch {}
        }
        return raw;
      })();
     

      const sessionId = (
         normalizedFeeLookup?.FeeInfos?.[0]?.MgiTransactionSessionId
      ) as string | undefined;

      const profilePreferenceId = billPayGetAuthConsumerResponseStore?.ConsumerInfo?.BillerLookupInfos?.[0]?.ProfilePreferenceId;

      const formatDob = (): string | undefined => {
        if (senderDOBStringFromStore) return senderDOBStringFromStore as string;
        const d = senderDOBFromStore as Date | null;
        if (d && !isNaN(d as any)) {
          const mm = String(d.getMonth() + 1).padStart(2, "0");
          const dd = String(d.getDate()).padStart(2, "0");
          const yyyy = d.getFullYear();
          return `${mm}/${dd}/${yyyy}`;
        }
        return undefined;
      };

      let payload: any = {}
      if (isNewConsumer) {
        payload = {
          unitProfileID: unitProfileId,
          agentID: agentId,
          productVariant: 'EP',
          amount: amountNumber,
          destinationCountry: 'USA',
          receiveCode,
          receiveAgentID,
          billerAccountNumber: billPayBillerAccountNumberStore,
          senderFirstName: senderFirstNameFromStore || billPayGetAuthConsumerResponseStore?.ConsumerInfo?.SenderFirstName,
          senderLastName: senderLastNameFromStore || billPayGetAuthConsumerResponseStore?.ConsumerInfo?.SenderLastName,
          senderAddress: senderAddressFromStore,
          senderCity: senderCityFromStore,
          senderState: senderStateFromStore,
          senderZipCode: senderZipCodeFromStore,
          senderCountry: senderCountryFromStore || 'USA',
          senderHomePhone: phoneNumberFromStore,
          senderDOB: formatDob(),
          accountNumberRetryCount: 1,
          sendCurrency: 'USD',
          receiveCurrency: 'USD',
          cardSwiped: false,
          mgiTransactionSessionID: sessionId,
          formFreeStaging: true,
          consumerId: '0', //effectiveConsumerId,
          // consumerProfileUpdate: true,
          // profilePreferenceId: profilePreferenceId,
          panAvailable: false,
        // consumerRegistration: reg
        };
        // don't add consumer registration if user skips PIN
        if (!skipUpdate) {
          const reg = new ConsumerRegistration();
          reg.consumerLoginID = phoneNumberFromStore;
          reg.item = consumerPin;
          reg.itemElementName = 'ConsumerPin';
          payload.consumerRegistration = reg;
        }
      } else {
        payload = {
          unitProfileID: unitProfileId,
          agentID: agentId,
          productVariant: 'EP',
          amount: amountNumber,
          destinationCountry: 'USA',
          receiveCode,
          receiveAgentID,
          billerAccountNumber: billPayBillerAccountNumberStore,
          senderFirstName: senderFirstNameFromStore || billPayGetAuthConsumerResponseStore?.ConsumerInfo?.SenderFirstName,
          senderLastName: senderLastNameFromStore || billPayGetAuthConsumerResponseStore?.ConsumerInfo?.SenderLastName,
          senderAddress: senderAddressFromStore,
          senderCity: senderCityFromStore,
          senderState: senderStateFromStore,
          senderZipCode: senderZipCodeFromStore,
          senderCountry: senderCountryFromStore || 'USA',
          senderHomePhone: phoneNumberFromStore,
          senderDOB: formatDob(),
          accountNumberRetryCount: 1,
          sendCurrency: 'USD',
          receiveCurrency: 'USD',
          cardSwiped: false,
          mgiTransactionSessionID: sessionId,
          formFreeStaging: true,
          consumerId: consumerIdFromStore ? String(consumerIdFromStore) : undefined,
          consumerProfileUpdate: true,
          profilePreferenceId: profilePreferenceId,
          panAvailable: false,
        };
      }

      try {
        const billerName = selectedBillerInformation?.BillerName;
        const senderDisplayName = [
          billPayGetAuthConsumerResponseStore?.ConsumerInfo?.SenderFirstName,
          billPayGetAuthConsumerResponseStore?.ConsumerInfo?.SenderLastName,
        ]
          .filter(Boolean)
          .join(" ");
        const validationPayload = withKioskLogContext(
          buildBillPayValidationPayload(payload as any),
          {
            senderName: senderDisplayName || undefined,
            receiverName: billerName,
          },
        );
        const validationResp = await callValidateBillPay(validationPayload);
        try { setBillPayValidationResponseStore?.(validationResp); } catch {}

        const noError = handleError(validationResp);
        if (noError === false) {
          return false; // stop progression on validation error
        }

        try {
          const respAny: any = validationResp || {};
          const conf = respAny?.confirmationNumber || respAny?.ConfirmationNumber;
          if (conf) { try { setConfirmationNumber?.(String(conf)); } catch { } }
          const respSessionId = respAny?.mgiTransactionSessionId || respAny?.MgiTransactionSessionId || respAny?.mgiTransactionSessionID || respAny?.MgiTransactionSessionID;
          if (respSessionId) { try { setMgiTransactionSessionId?.(String(respSessionId)); } catch { } }
        } catch (e: any) {
          const msg = String(e?.message ?? t("ERROR_VALIDATION_FAILED"));
          try { showError(msg); } catch {}
          Alert.alert(t('ERROR') || t('ERROR'), msg, [{ text: t('STRKEY_OK') }]);
          return false;
        }
      } catch (e: any) {
        const msg = String(e?.message ?? t("ERROR_AN_ERROR_OCCURED"));
        try { showError(msg); } catch {}
        Alert.alert(t('ERROR') || t('ERROR'), msg, [{ text: t('STRKEY_OK') }]);
        return false; // stay on this step
      }
    } catch (e: any) {
      const msg = String(e?.message ?? t("ERROR_AN_ERROR_OCCURED"));
      try { showError(msg); } catch {}
      Alert.alert(t('ERROR') || t('ERROR'), msg, [{ text: t('STRKEY_OK') }]);
      return false; // stay on this step
    }
    return true;
  };

  const handleError = (resp: any) => {
    try {
      let raw: any = (resp && (resp as any).data !== undefined)
        ? (resp as any).data
        : resp;
      if (typeof raw === 'string') {
        try { raw = JSON.parse(raw); } catch {}
      }
      const errDetailRaw: any = raw?.ErrorDetail ?? raw?.errorDetail ?? null;
      const errDetail: any = Array.isArray(errDetailRaw) ? errDetailRaw[0] : errDetailRaw;
      if (errDetail) {
        const code = errDetail?.ErrorCode ?? errDetail?.errorCode ?? '';
        const errStr = errDetail?.ErrorString ?? errDetail?.errorString ?? '';
        const field = errDetail?.OffendingField ?? errDetail?.offendingField ?? '';
        const message = `${t("ERROR_THERE_WAS_A_PROBLEM_WITH_YOUR_TRANSACTION")} ${[code, errStr, field].filter(Boolean).join(' ')}`;
        try { showError(message); } catch {}
        Alert.alert(t('ERROR') || t('ERROR'), message, [{ text: t("STRKEY_OK") }]);
        return false; // stop further processing on validation error
      }
      return true; // no error
    } catch (e) {
      const msg = String((e as any)?.message ?? t("ERROR_AN_ERROR_OCCURED"));
      try { showError(msg); } catch {}
      Alert.alert(t('ERROR') || t('ERROR'), msg, [{ text: t("STRKEY_OK") }]);
      return false; // stay on this step
    }
  }

  const availableNotes = useMemo(() => {
      try {
        const raw: any = billPayBillerSearchResponseStore as any;
        const container: any = (raw?.BillerInfo ?? raw?.billerInfo ?? raw) as any;
        const item: any = (container?.BillerInfos?.[0] ?? container?.billerInfos?.[0] ?? container) as any;
        const notes: string = String(item?.BillerNotes ?? item?.billerNotes ?? '').trim();
        return notes;
      } catch {
        return '';
      }
    }, [billPayBillerSearchResponseStore]);

  const handleSkip = () => {
    // Run validation + transaction only, then jump to HeadToCashier
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    executeWithLoading(async () => {
     try {
       capturePosthogEvent('Bill Pay Flow - Continue as Guest', {
        "data-mg-skip-flow": "true",
        "data-mg-phone-number": phoneNumberFromStore,
      });
        const result = await handleBillPayFinalReview({ skipUpdateProfile: true });
        if (!result) {
          // An error was shown; stay on current step
          return;
        }
      } catch (e) {
        console.warn('Skip flow failed', e);
      }
      const targetIndex = steps.findIndex(s => s.key === "billPayHeadToCashier");
      if (targetIndex !== -1) {
        try { recordStepTransition?.("billPayHeadToCashier"); } catch { }
        setCurrentStep(targetIndex);
      }
    }, t('STRKEY_PROCESSING'));
  };

  // Enable Continue button; steps that manage validation via onValidationChange start false
  useEffect(() => {
    const key = steps[currentStep]?.key;
    if (key === "phone" || key === "additionalInformation") {
      setIsStepValid(false);
    } else {
      setIsStepValid(true);
    }
  }, [currentStep]);

  // Build a Bill Pay validation draft including AdditionalInformation values
  const buildBillPayValidationDraft = (): any => {
    const normalizedFeeLookup: any = (() => {
      let raw: any = billPayFeeLookupResponseStore as any;
      if (typeof raw === 'string') {
        try { raw = JSON.parse(raw); } catch {}
      }
      return raw;
    })();
    const sessionId = (
      normalizedFeeLookup?.FeeInfos?.[0]?.MgiTransactionSessionId
    ) as string | undefined;
    const billerInfo: any = (billPayBillerSearchResponseStore?.BillerInfo ?? billPayBillerSearchResponseStore?.billerInfo ?? billPayBillerSearchResponseStore) as any;
    const receiveCode = billerInfo?.BillerInfos?.[0]?.ReceiveCode;
    const receiveAgentID = billerInfo?.BillerInfos?.[0]?.ReceiveAgentId;
    const amountNumber = Number(String(currentSendAmount || '0').replace(/,/g, '')) || 0;
    const payloadDraft = buildBillPayValidationPayload({
      unitProfileID: unitProfileId,
      agentID: agentId,
      productVariant: 'EP',
      amount: amountNumber,
      destinationCountry: 'USA',
      receiveCode,
      receiveAgentID,
      billerAccountNumber: billPayBillerAccountNumberStore,
      senderFirstName: senderFirstNameFromStore,
      senderLastName: senderLastNameFromStore,
      senderAddress: senderAddressFromStore,
      senderCity: senderCityFromStore,
      senderState: senderStateFromStore,
      senderZipCode: senderZipCodeFromStore,
      senderCountry: senderCountryFromStore || 'USA',
      senderHomePhone: phoneNumberFromStore,
      senderDOB: ((): string | undefined => {
        if (senderDOBStringFromStore) return senderDOBStringFromStore as string;
        const d = senderDOBFromStore as Date | null;
        if (d && !isNaN(d as any)) {
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          const yyyy = d.getFullYear();
          return `${mm}/${dd}/${yyyy}`;
        }
        return undefined;
      })(),
      accountNumberRetryCount: 1,
      sendCurrency: 'USD',
      receiveCurrency: 'USD',
      cardSwiped: false,
      mgiTransactionSessionID: sessionId,
      formFreeStaging: true,
      consumerId: consumerIdFromStore ? String(consumerIdFromStore) : undefined,
      consumerProfileUpdate: true,
      profilePreferenceId: undefined,
      panAvailable: false,
    } as any);
    return payloadDraft;
  };

  // Compute missing required fields like Send's missingFieldsFound flow
  const computeBillPayMissingRequiredFields = (): any[] => {
    const flatFields = billPayAllRequiredFieldsStore as any[] | null;
    const validationReqDraft = buildBillPayValidationDraft();
    return new RequiredFields().getMissingFieldsForBP(flatFields || [], validationReqDraft);
  };

  const syncBillPayMissingRequiredFieldsIntoStore = (): any[] => {
    try {
      const missing = computeBillPayMissingRequiredFields();
      try { setMissingRequiredFields?.(missing as any); } catch {}
      return missing || [];
    } catch {
      try { setMissingRequiredFields?.([]); } catch {}
      return [];
    }
  };

  /** Drop additional-information answers when amount or biller changes; advance pricing context. */
  const applyBillPayPricingContextUpdate = (): boolean => {
    const next = getBillPayPricingFingerprint(
      currentSendAmount,
      billPayBillerSearchResponseStore,
      selectedBillerInformation,
    );
    const prev = billPayAdditionalPricingContextRef.current;
    const cleared = invalidateBillPayAdditionalInformationOnPricingChange(prev, next);
    billPayAdditionalPricingContextRef.current = next;
    return cleared;
  };

  const handleBack = () => {
    const curKey = steps[currentStep]?.key;
    if (curKey === "phone") {
      handleClose?.();
      return;
    }
    if (currentStep <= 0) return;
    const prevIdx = currentStep - 1;
    const prevKey = steps[prevIdx]?.key;

    if (
      curKey === "fraudPrevention" &&
      prevKey === "additionalInformation" &&
      skippedAdditionalInformationBeforeFraudRef.current
    ) {
      const addressIdx = steps.findIndex((s) => s.key === "billPayAddress");
      if (addressIdx !== -1) {
        try { recordStepTransition?.("billPayAddress"); } catch {}
        setCurrentStep(addressIdx);
        return;
      }
      const dobIdx = steps.findIndex((s) => s.key === "billPayDOB");
      if (dobIdx !== -1) {
        try { recordStepTransition?.("billPayDOB"); } catch {}
        setCurrentStep(dobIdx);
        return;
      }
    }
    if (
      curKey === "fraudPrevention" &&
      prevKey === "billPayFullName" &&
      !skippedAdditionalInformationBeforeFraudRef.current &&
      steps === NEW_CONSUMER_STEPS
    ) {
      const aiIdx = steps.findIndex((s) => s.key === "additionalInformation");
      if (aiIdx !== -1) {
        try { recordStepTransition?.("additionalInformation"); } catch {}
        setCurrentStep(aiIdx);
        return;
      }
    }
    if (
      curKey === "additionalInformation" &&
      additionalInformationNavSourceRef.current === "billPayFinalReview"
    ) {
      const frIdx = steps.findIndex((s) => s.key === "billPayFinalReview");
      if (frIdx !== -1) {
        try { recordStepTransition?.("billPayFinalReview"); } catch {}
        additionalInformationNavSourceRef.current = null;
        setCurrentStep(frIdx);
        return;
      }
    }
    if (
      curKey === "additionalInformation" &&
      additionalInformationNavSourceRef.current === "billPayDOB"
    ) {
      const dobIdx = steps.findIndex((s) => s.key === "billPayDOB");
      if (dobIdx !== -1) {
        try { recordStepTransition?.("billPayDOB"); } catch {}
        additionalInformationNavSourceRef.current = null;
        setCurrentStep(dobIdx);
        return;
      }
    }
    if (
      curKey === "additionalInformation" &&
      additionalInformationNavSourceRef.current === "billPayFullName"
    ) {
      const fnIdx = steps.findIndex((s) => s.key === "billPayFullName");
      if (fnIdx !== -1) {
        try { recordStepTransition?.("billPayFullName"); } catch {}
        setReturnToStep(null);
        additionalInformationNavSourceRef.current = null;
        setCurrentStep(fnIdx);
        return;
      }
    }
    if (
      curKey === "additionalInformation" &&
      additionalInformationNavSourceRef.current === "billPayAddress"
    ) {
      const addressIdx = steps.findIndex((s) => s.key === "billPayAddress");
      if (addressIdx !== -1) {
        try { recordStepTransition?.("billPayAddress"); } catch {}
        setReturnToStep(null);
        additionalInformationNavSourceRef.current = null;
        setCurrentStep(addressIdx);
        return;
      }
    }
    setCurrentStep((prev) => prev - 1);
  };

  const handleCustomerNotFound = () => {
    const targetIndex = steps.findIndex(s => s.key === "previosBillerInfo");
    if (targetIndex !== -1) {
      setCurrentStep(targetIndex);
    }
  };

  const handleBillerSelected = (_biller: any) => {
    const billerName = selectedBillerInformation?.BillerName;
    captureBillPayPreviousBillerEvent(billerName)
    if (applyBillPayPricingContextUpdate()) {
      try { setMissingRequiredFields?.([]); } catch {}
    }
    const targetIndex = steps.findIndex(s => s.key === "accountInformation");
    if (targetIndex !== -1) {
      try { recordStepTransition?.("accountInformation"); } catch { }
      setCurrentStep(targetIndex);
    }
  };

  const closeNeedHelp = () => {
    setNeedHelp(false);
  }

  const handleNeedHelpCall = () => {
    // Calling to provided number
  }

  
  const formatDateForPOPup = () => {
    try {
      // Always read latest from store to avoid stale closures
      const { senderDOB: dobFromState, senderDOBString } = useSessionStore.getState() as any;
      
      // Prefer string format to avoid timezone issues from Date objects
      // This ensures consistent display regardless of how the date was originally parsed
      const rawString = (typeof dobFromState === 'string' ? dobFromState : undefined) || (senderDOBString as string | undefined) || (senderDOBStringFromStore as string | undefined);
      
      let date: Date | undefined = undefined;
      
      if (rawString) {
        const s = String(rawString).trim();
        if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
          // YYYY-MM-DD or ISO; create at noon to avoid TZ shifts
          const y = Number(s.slice(0, 4));
          const m = Number(s.slice(5, 7)) - 1;
          const d = Number(s.slice(8, 10));
          date = new Date(y, m, d, 12, 0, 0, 0);
        } else if (/^(\d{2})\/(\d{2})\/(\d{4})$/.test(s)) {
          // Handle both MM/DD/YYYY and DD/MM/YYYY
          const [, p1, p2, yyyy] = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/) as RegExpMatchArray;
          const n1 = Number(p1);
          const n2 = Number(p2);
          let mm = n1;
          let dd = n2;
          // If first part > 12, it's DD/MM/YYYY; swap
          if (n1 > 12 && n2 <= 12) {
            mm = n2;
            dd = n1;
          }
          // Create date at noon local time to avoid timezone boundary issues
          date = new Date(Number(yyyy), mm - 1, dd, 12, 0, 0, 0);
        } else {
          const tmp = new Date(s);
          if (!isNaN(tmp as any)) date = tmp;
        }
      }
      
      // Fallback to Date object from store if string is not available
      if (!date) {
        const asDate: any = dobFromState instanceof Date ? dobFromState : undefined;
        date = asDate && !isNaN(asDate as any) ? (asDate as Date) : undefined;
      }

      if (!date || isNaN(date as any)) return '';
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch {
      return '';
    }
  };

  const renderDOBPopUp = () => (
    <View style={styles.root}>
      <TouchableOpacity
        style={styles.scrim}
        activeOpacity={1}
        onPress={() => setIsDOBCOnfirm(false)}
      />
      <View style={styles.bottomSheet}>
        <TouchableOpacity
          style={styles.modalHeaderRow}
          onPress={() => {
            setIsDOBCOnfirm(false);
          }}
          testID="senderDOBClose"
        >
          <CloseIcon width={24} height={24} />
        </TouchableOpacity>
        <View style={styles.modalBodyCenter}>
          <CalenderContainer width={64} height={64} />
          <Text style={styles.modalTitleDOB}>
            {t("SENDER_DOB_CONFIRMATION_TITLE")}
          </Text>
          <Text style={styles.modalInfo}>
            <Trans
              i18nKey="SENDER_DOB_CONFIRM_SUBTITLE"
              components={{
                legalId: (
                  <Text
                    style={[styles.modalInfo, { fontWeight: "700" }]}
                  />
                ),
              }}
            />
          </Text>
          <Text style={styles.modalDOB}>{formatDateForPOPup()}</Text>
        </View>
        <View style={styles.dobModalFooter}>
          <TouchableOpacity
            testID="senderDOBConfirm"
            style={styles.confirmBtn}
            onPress={() => {
              setIsDOBCOnfirm(false);
              handleBillPayDOBNavigation();
            }}
          >
            <Text style={[styles.cancelText, { color: Colors.white }]}>
              {t("CONFIRM")}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            testID="senderDOBEdit"
            style={styles.cancelBtn}
            onPress={() => setIsDOBCOnfirm(false)}
          >
            <Text style={[styles.cancelText, { lineHeight: 24 }]}>
              {t("EDIT")}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );


  if (!StepComponent) {
    return <View style={styles.container} />;
  }

  return (
    <KeyboardAvoidingView
        style={[
          styles.container,
          Platform.OS === "web" && containerHeight != null && containerHeight > 0
            ? {
                height: containerHeight,
                minHeight: containerHeight,
                maxHeight: containerHeight,
              }
            : containerHeight != null && { height: containerHeight },
        ]}
        behavior={
          Platform.OS === "web"
            ? undefined
            : stickBillPayFooterAboveKeyboard
              ? "padding"
              : undefined
        }
        enabled={Platform.OS === "web" ? false : stickBillPayFooterAboveKeyboard}
        keyboardVerticalOffset={Platform.OS === "ios" && stickBillPayFooterAboveKeyboard ? 60 : 0}
      >
        <View style={styles.mainWrapper}>
          {/* --- HEADER (sticky on web) --- */}
          <View
            style={[styles.header, Platform.OS === "web" && styles.headerFixed]}
            onLayout={(e: LayoutChangeEvent) => setHeaderHeight(e.nativeEvent.layout.height)}
          >
            {/* Top Bar */}
            <View style={styles.topBar}>
              {steps[currentStep].key !== "billPayHeadToCashier" ? (
                <TouchableOpacity style={styles.topBarButton} onPress={handleBack}
                testID="backButton">
                  <BackIcon width={24} height={24}/>
                  {/* <Image source={BackIcon} style={styles.IconStyle} /> */}
                </TouchableOpacity>
              ) : (
                <Text></Text>
              )
              }
              <View style={styles.topBarRight}>
                <TouchableOpacity style={styles.topBarButton} onPress={() => setNeedHelp(true)}
                testID="helpButton">
                  <HelpIcon width={24} height={24}/>
                </TouchableOpacity>
                <TouchableOpacity
                  testID="closeButton"
                  style={[styles.topBarButton, { marginLeft: 10}]}
                  onPress={handleClose}
                >
                  <CloseIcon width={24} height={24}/>
                </TouchableOpacity>
              </View>
            </View>

            {/* Progress Bar */}
            <View style={styles.progressRow}>
              {steps.map((_, i) => (
                <View
                  key={i}
                  testID={`progress-segment-${i}`}
                  style={[
                  styles.progressSegment,
                  { borderTopLeftRadius: i === 0 ? 9 : 0 },
                  { borderBottomLeftRadius: i === 0 ? 9 : 0 },
                  { borderTopRightRadius: i === steps.length - 1 ? 9 : 0 },
                  { borderBottomRightRadius: i === steps.length - 1 ? 9 : 0 },
                  { backgroundColor: i <= currentStep ? Colors.titleTextColor : Colors.searchBackground },
                ]}
                />
              ))}
            </View>
          </View>
          {showHeaderSpacer && (
            <View style={[styles.headerSpacer, { height: headerHeight }]} />
          )}

          {/* --- STEP CONTENT --- */}
            {(steps[currentStep].key === "previosBillerInfo") ? (
              <View style={styles.stepAreaContainer}>
                <AnyStepComponent
                  testID="step-previosBillerInfo"
                  onValidationChange={handleValidationChange}
                  onSelectBiller={handleBillerSelected}
                />
              </View>
            ) : steps[currentStep].key === "billPayAddress" ||
              steps[currentStep].key === "billPayFullName" ? (
              (() => {
                const stepAreaHeight = scrollAreaHeight;
                const hasHeight = stepAreaHeight != null && stepAreaHeight > 0;
                return (
                  <View
                    style={[
                      styles.stepAreaContainer,
                      hasHeight && {
                        height: stepAreaHeight,
                        maxHeight: stepAreaHeight,
                      },
                      !hasHeight &&
                        Platform.OS === "web" &&
                        styles.stepAreaContainerWebFallback,
                    ]}
                  >
                    {steps[currentStep].key === "billPayAddress" ? (
                      <AnyStepComponent
                        testID="step-billPayAddress"
                        ref={BillPayAddressStepRef}
                        onValidationChange={handleValidationChange}
                        onContinue={handleNext}
                      />
                    ) : (
                      <AnyStepComponent
                        testID="step-billPayFullName"
                        ref={BillPayEnterNameStepRef}
                        onValidationChange={handleValidationChange}
                        onContinue={handleNext}
                      />
                    )}
                  </View>
                );
              })()
            ) : (
            <ScrollView
              ref={scrollRef}
              style={[
                styles.scrollView,
                scrollAreaHeight != null && scrollAreaHeight > 0 && { maxHeight: scrollAreaHeight },
                isBillPayReviewOrFinalReviewScroll && { backgroundColor: Colors.white },
              ]}
              contentContainerStyle={
                isBillPayReviewOrFinalReviewScroll
                  ? {
                      paddingBottom: isGradientExpand ? 340 : 220,
                      backgroundColor: Colors.white,
                    }
                  : steps[currentStep].key === "payAmount"
                    ? {
                        flexGrow: 1,
                        paddingBottom: footerHeight > 0 ? footerHeight : 130,
                      }
                    : (steps[currentStep].key === "pin" || steps[currentStep].key === "confirmPin")
                      ? styles.scrollContainerPinSteps
                      : (steps[currentStep].key === "billPayHeadToCashier" ? styles.scrollContainerHeadToCashier : (steps[currentStep].key === "fraudPrevention" ? styles.scrollContainerFraudPrevention : Platform.OS === "web" && !stickBillPayFooterAboveKeyboard && {
                        paddingBottom: footerHeight || 100,
                      }))
              }
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={false}
              bounces={
                !(
                  isBillPayReviewOrFinalReviewScroll ||
                  steps[currentStep].key === "payAmount"
                )
              }
            >
            {(steps[currentStep].key === "pin" || steps[currentStep].key === "confirmPin") ? (
              <AnyStepComponent
                testID={steps[currentStep].key === "pin" ? "step-pin" : "step-confirmPin"}
                onValidationChange={handleValidationChange}
                handleForgotPassword={handleForgotPassword}
                attemptsLeft={attemptsLeft}
                locked={locked}
                showAttemptsWarning={showPinAttemptsWarning}
                apiError={steps[currentStep].key === "pin" ? (pinStepApiError ?? undefined) : undefined}
                resetSignal={resetSignal}
                onContinue={handleNext}
              />
            ) : (steps[currentStep].key === "reviewAndEdit") || (steps[currentStep].key === "billPayFinalReview") ? (
              <AnyStepComponent 
                testID={steps[currentStep].key === "reviewAndEdit" ? "step-reviewAndEdit" : "step-billPayFinalReview"}
                onEditStep={handleEditStep} 
                onContinue={handleNext} 
              />
            ) : (steps[currentStep].key === "payAmount") ? (
              <AnyStepComponent
                testID="step-payAmount"
                onContinue={handleNext}
                onEditStep={handleEditStep}
                onValidationChange={handleValidationChange}
              />
            ) : (steps[currentStep].key === "phone") ? (
              <AnyStepComponent
                testID="step-phone"
                ref={PhoneStepRef}
                onValidationChange={handleValidationChange}
              />
            ) : (steps[currentStep].key === "billPayDOB") ? (
              <AnyStepComponent
                testID="step-billPayDOB"
                ref={BillPayDOBStepRef}
                onContinue={handleNext}
              />
            ) :(steps[currentStep].key === "accountInformation") ? (
              <AnyStepComponent
                testID="step-accountInformation"
                ref={AccountInformationStepRef}
                onContinue={handleNext}
              />
            ) : (steps[currentStep].key === "fraudPrevention") ? (
              <AnyStepComponent
                testID="step-fraudPrevention"
                onValidationChange={handleValidationChange}
                onContinue={handleNext}
              />
            ) : (steps[currentStep].key === "additionalInformation") ? (
              <AnyStepComponent
                ref={AdditionalInformationStepRef}
                testID="step-additionalInformation"
                onValidationChange={handleValidationChange}
                onContinue={handleNext}
              />
            ) : (steps[currentStep].key === "pinCreated") ? (
              <AnyStepComponent
                testID="step-pinCreated"
              />
            ) : (steps[currentStep].key === "billPayHeadToCashier") ? (
              <AnyStepComponent
                testID="step-billPayHeadToCashier"
              />
            ) : (
              <AnyStepComponent
                onValidationChange={handleValidationChange}
              />
            )}
          </ScrollView>
            )}
          <Modal
          visible={needHelp}
          transparent
          animationType="none"
          // onRequestClose={() => setShowPopup(false)}
          onRequestClose={() => closeNeedHelp?.()}
        >
          <NeedHelpComponent
            closeNeedHelp={closeNeedHelp}
            handleNeedHelpCall={handleNeedHelpCall}
          />
        </Modal>
        <Modal
          visible={isDOBConfirm}
          transparent
          animationType="none"
          onRequestClose={() => setIsDOBCOnfirm(false)}
        >
          {renderDOBPopUp()}
        </Modal>
        <Modal
          visible={showPopup}
          transparent
          animationType="none"
          onRequestClose={() => setShowPopup(false)}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.bottomSheet}>
              <TouchableOpacity
                onPress={() => setShowPopup(false)}
                style={styles.modalHeaderRow}
                onFocus={(e: any) => e?.target?.blur?.()}
              >
                <CloseIcon width={24} height={24}/>
              </TouchableOpacity>
              <View style={{ alignItems:'center' }}>
                <AccountLockedIcon width={64} height={64}/>
                <Text style={styles.accountLock}>
                  {t("YOUR_ACCOUNT_LOCKED")}
                </Text>
                <Text style={styles.accountLockInfo}>
                  <Trans
                    i18nKey="YOU_CAN_CONTINUE_AS_GUEST"
                    components={{
                      guest: (
                        <Text
                          style={[
                            styles.accountLockInfo,
                            { fontWeight: "700" },
                          ]}
                        />
                      ),
                    }}
                  />
                </Text>
              </View>
              <TouchableOpacity
                style={styles.button}
                testID="continueAsGuestButton"
                onPress={() => handleForgotPassword?.()}
                onFocus={(e: any) => e?.target?.blur?.()}
              >
                <Text style={styles.buttonText}>
                  {t("CONTINUE_AS_GUEST")}
                </Text>
              </TouchableOpacity>
              {/* <TouchableOpacity
                testID="resetPinButton"
                style={styles.cancelBtn}
                onPress={handleResetPin}
                onFocus={(e: any) => e?.target?.blur?.()}
              >
                <Text style={[styles.cancelText, { lineHeight: 24 }]}>{t("RESET_PIN")}</Text>
              </TouchableOpacity> */}
            </View>
          </View>
        </Modal>
          {/* Modal for reference number result */}
          <Modal
            visible={showRefResultModal}
            transparent
            animationType="none"
            onRequestClose={() => setShowRefResultModal(false)}
          >
            <View style={styles.modalOverlay}>
              <View style={styles.modalContainer}>
                <Text style={styles.modalTitle}>{t("TRANSACTION_DETAILS")}</Text>
                <View style={styles.modalDivider} />
                <View style={{ marginTop: 12 }}>
                  <Text style={styles.modalRowLabel}>{t("TRANSACTION_RECEIVER_NAME")}</Text>
                  <Text style={styles.modalRowValue}>{refResult?.receiverName || '-'}</Text>
                  <Text style={[styles.modalRowLabel, { marginTop: 8 }]}>{t("TRANSACTION_SENDER_NAME")}</Text>
                  <Text style={styles.modalRowValue}>{refResult?.senderName || '-'}</Text>
                  <Text style={[styles.modalRowLabel, { marginTop: 8 }]}>{t("RECEIVE_AMOUNT")}</Text>
                  <Text style={styles.modalRowValue}>{String(refResult?.receiveAmount ?? '-')}</Text>
                  <Text style={[styles.modalRowLabel, { marginTop: 8 }]}>{t("REFERENCE_NUMBER")}</Text>
                  <Text style={styles.modalRowValue}>{String(refResult?.referenceNumber ?? '-')}</Text>
                </View>
                <TouchableOpacity style={[styles.button, { marginTop: 16 }]} onPress={() => setShowRefResultModal(false)}
                  testID="okButton">
                  <Text style={styles.buttonText}>{t("STRKEY_OK")}</Text>
                </TouchableOpacity>
              </View>
            </View>
          </Modal>
          {/* --- FIXED FOOTER --- */}
          {hasFooterForCurrentBillPayStep && (
          <View
            style={[
              styles.footerContainer,
              styles.footerShrink,
              (steps[currentStep].key === "pin" || steps[currentStep].key === "confirmPin") && styles.footerContainerPinSteps,
              Platform.OS === "web" &&
              !stickBillPayFooterAboveKeyboard &&
              styles.footerContainerWebViewportPinned,
            ]}
            onLayout={(e: LayoutChangeEvent) => setFooterHeight(e.nativeEvent.layout.height)}
          >
            {(steps[currentStep].key !== "previosBillerInfo") &&
            (steps[currentStep].key !== "pin") && 
            (steps[currentStep].key !== "confirmPin") &&  (
              <TouchableOpacity
                testID="continueButton"
                style={[
                  styles.button,
                  { backgroundColor: Colors.secondaryText },
                ]}
                onPress={() => handleNext()}
                // disabled={!isStepValid}
              >
                <Text style={styles.buttonText}>
                  {currentStep === steps.length - 1 ? t("I_AM_FINISHED") : t("STRKEY_CONTINUE")}
                </Text>
              </TouchableOpacity>
            )}
            {(steps[currentStep].key === "pin" ||
            steps[currentStep].key === "confirmPin") && (
              <TouchableOpacity onPress={steps === EXISTING_CUNSUMER_STEPS ? handleForgotPassword : handleSkip } style={{ marginTop: 8, alignItems: "center" }}
                testID="continueAsGuestButton">
                <Text style={styles.contniueAsGuest}>
                  {steps === EXISTING_CUNSUMER_STEPS ? t("CONTINUE_AS_GUEST") : t("SKIP")}
                </Text>
              </TouchableOpacity>
            )}
            {(steps[currentStep].key === "receiverReferenceNumber") && (
              <TouchableOpacity onPress={handleSkip} style={{ marginTop: 8, alignItems: "center" }}
                testID="disclaimerButton">
                <Text style={{ color: "#2563EB", textDecorationLine: "underline" }}>{t("DISCLAIMER")}</Text>
              </TouchableOpacity>
            )}
          
          </View>
          )}
          {
            (steps[currentStep].key === "reviewAndEdit" ||
            steps[currentStep].key === "billPayFinalReview" ||
            (steps[currentStep].key === "payAmount")
            ) &&
             (<View
              style={styles.footerShrink}
              onLayout={(e: LayoutChangeEvent) =>
                setFooterHeight(e.nativeEvent.layout.height)
              }
            >
              <GradientBackground style={styles.gradientFooter}>
              <View style={{ paddingHorizontal: 24}}>
                {
                  (steps[currentStep].key === "payAmount") ? (
                    
                    <View style={[styles.gradientRow, { marginTop: 24}]}>
                      <Text style={[styles.gradientPrice, { textDecorationLine: 'underline'}]}>{t("REVIEW_TOTAL")}:</Text>
                      <View style={{ flexDirection: 'row'}}>
                        <Text style={styles.gradientPrice}>{sendAmount} USD</Text>
                      </View>
                    </View>
                    
                  ) : (
                    <GradientExpandSwipeArea
                      isExpanded={isGradientExpand}
                      onExpand={() => setIsGradientExpand(true)}
                      onCollapse={() => setIsGradientExpand(false)}
                      onToggle={() => setIsGradientExpand(!isGradientExpand)}
                      handleStyle={styles.viewTap}
                    >
                    { isGradientExpand &&
                    <View>
                        <View style={styles.gradientRow}>
                          <Text style={styles.gradientFeeLabel}>{t("AVAILABLE")}</Text>
                          <View style={{ flexDirection: 'row'}}>
                            <Text style={styles.oldPrice}>{availableNotes || ' '}</Text>
                          </View>
                        </View>
                        <View style={styles.gradientRow}>
                          <Text style={styles.gradientFeeLabel}>{t("FEES")}</Text>
                          <View style={{ flexDirection: 'row'}}>
                            <Text style={styles.oldPrice}>{totalsFromFeeLookup.totalFees.toFixed(2)} {summary.sendCurrency}</Text>
                          </View>
                        </View>
                    </View>}
                    <View style={[styles.gradientRow, { marginBottom: 16 }]}>
                       <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <WarningBlackIcon width={16} height={16}/>
                        <Text style={[styles.gradientPrice, {marginLeft: 8}]}>{t("REVIEW_TOTAL")}</Text>
                      </View>
                      <View style={{ flexDirection: 'row'}}>
                        <Text style={styles.gradientPrice}>{totalsFromFeeLookup.totalAmount.toFixed(2)} {summary.sendCurrency}</Text>
                      </View>
                    </View>
                    </GradientExpandSwipeArea>
                  )
                }
                <TouchableOpacity testID="continueButton"
                  onPress={() => handleNext()}
                  activeOpacity={0.8}
                  style={styles.gradientContinueButton}
                >
                  <Text style={styles.gradientContinueButtonText}>
                    {steps[currentStep].key === "billPayFinalReview" ? t("STRKEY_SUBMIT") : t("STRKEY_CONTINUE")}
                  </Text>
                </TouchableOpacity>
                </View>
              </GradientBackground>
            </View>)
          }
        </View>
      </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
    backgroundColor: Colors.white,
    ...(Platform.OS === 'web' && {
      // dvh tracks mobile browser chrome better than vh (iOS Safari URL bar)
      minHeight: '100dvh' as any,
      height: '100dvh' as any,
      maxHeight: '100dvh' as any,
      overflow: 'hidden' as any,
    })
  },
  header: {
    paddingHorizontal: 24,
    flexShrink: 0,
  },
  headerFixed: {
    ...(Platform.OS === "web" && {
      position: "fixed" as any,
      top: 0,
      left: 0,
      right: 0,
      zIndex: 10,
      backgroundColor: Colors.white,
    }),
  },
  headerSpacer: {
    ...(Platform.OS === "web" && {
      height: 80,
      width: "100%",
    }),
  },
  content: {
    flex: 1,
  },
  mainWrapper: {
    flex: 1,
    flexDirection: "column",
    ...(Platform.OS === 'web' && {
      minHeight: 0 as any,
      overflow: 'hidden' as any,
    }),
  },
  scrollView: {
    flex: 1,
    ...(Platform.OS === 'web' && {
      minHeight: 0 as any,
      overflow: 'auto' as any,
      overflowX: 'hidden' as any,
    })
  },
  /** Bill Pay address / full name: step owns scroll + inline Continue (same idea as send receiverName). */
  stepAreaContainer: {
    flex: 1,
    minHeight: 0,
    width: "100%",
    ...(Platform.OS === "web" && {
      overflow: "hidden" as any,
      overflowX: "hidden" as any,
    }),
  },
  stepAreaContainerWebFallback: {
    height: "100%" as any,
    minHeight: "50vh" as any,
  },
  scrollContainer: {
    flexGrow: 1,
    paddingBottom: 120, // space for footer; 24px effective gap after content via footer padding
  },
  // Pin/confirmPin: no flexGrow so content stays compact and footer (Skip button) stays visible without scrolling
  scrollContainerPinSteps: {
    paddingBottom: 24,
  },
  scrollContainerFraudPrevention: {
    flexGrow: 1,
    paddingBottom: 32, // only 32px after content before footer
  },
  scrollContainerHeadToCashier: {
    flexGrow: 1,
    paddingBottom: 32, // 32px space after HeadToCashier content before "I'm finished" button
  },
  // 24px gap between Terms and gradient + 24px space at bottom of content so nothing hides behind gradient
  scrollContainerWithGradientCollapsed: {
    paddingBottom: 154, // collapsed gradient ~100px + 24px gap + 24px bottom clearance (also used for payAmount step)
  },
  scrollContainerWithGradientExpanded: {
    paddingBottom: 200, // expanded gradient ~220px + 24px gap + 24px bottom clearance
  },
  footer: {
    paddingHorizontal: 24,
    paddingTop: 14,
    paddingBottom: 24,
    backgroundColor: Colors.white
  },
  footerShrink: {
    flexShrink: 0,
  },
  dropdownPriorityScrollLayer: {
    ...(Platform.OS === "web" && {
      position: "relative" as any,
      zIndex: 5,
      overflow: "visible" as any,
    }),
  },
  footerBehindDropdownLayer: {
    ...(Platform.OS === "web" && {
      position: "relative" as any,
      zIndex: 1,
    }),
  },
  footerContainer: {
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
    paddingTop: 14,
    /** Bottom gap under Continue: 24px plus safe-area on web (inset-only was wiping the 24px). Native: footer padding only — same as send (no button marginBottom). */
    ...(Platform.OS === "web"
      ? ({
          paddingBottom: "calc(24px + env(safe-area-inset-bottom, 0px))",
        } as any)
      : { paddingBottom: 24 }),
  },
  footerContainerWebViewportPinned: {
    position: "fixed" as any,
    left: 0,
    right: 0,
    width: "100%",
    zIndex: 999,
    bottom: 0,
  },
  // Pin/confirmPin: same 16px clearance as primary Continue (Skip / Continue as guest)
  footerContainerPinSteps: {
    paddingBottom: 16,
  },
  button: {
    width: "100%",             // button always full width
    backgroundColor: "#000",
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: "center",
    marginTop: 14,
    marginBottom: 0, // 24px under the button comes from footerContainer paddingBottom
  },
  contniueAsGuest: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '500',
    lineHeight: 20,
    color: Colors.titleTextColor,
  },
  topBar: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 16
  },
  topBarButton: {
    padding: 1
  },
  topBarRight: {
    flexDirection: "row"
  },
  IconStyle: {
    height: 24,
    width: 24
  },
  iconText: {
    fontSize: 20,
    color: "#111"
  },
  progressRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16,
    height: 6
  },
  progressSegment: {
    flex: 1,
    height: 4,
  },
  title: {
    fontSize: 20,
    fontWeight: "700",
    marginBottom: 16
  },
  buttonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600"
  },
  modalOverlay: {
    flex: 1,
    justifyContent: "flex-end",
    backgroundColor: "rgba(0,0,0,0.4)"
  },
  root: {
    flex: 1,
    justifyContent: "flex-end",
    position: "fixed" as any, // web only
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  scrim: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0,0,0,0.4)",
  },
  modalContent: {
    backgroundColor: Colors.white,
    padding: 24,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    width: "100%",
    zIndex: 2, // ensures it stays above scrim
  },
  modalContainer: {
    width: "88%",
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 16
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "700",
    textAlign: "center",
    marginBottom: 12
  },
  modalTitleDOB: {
    fontFamily: "Rubik",
    fontSize: 24,
    fontWeight: "500",
    color: Colors.accountLock,
    lineHeight: 28,
    textAlign: "center",
    marginVertical: 26,
  },
  accountLock: {
    fontSize: 24,
    fontWeight: "500",
    lineHeight: 28,
    fontFamily: 'Rubik',
    marginVertical: 26,
    color: Colors.accountLock,
  },
  accountLockInfo: {
    fontSize: 14,
    fontWeight: "400",
    lineHeight: 18,
    fontFamily: 'Rubik',
    marginBottom: 48,
    color: Colors.accountLock,
    textAlign: "center",
  },
  modalRowLabel: {
    fontSize: 14,
    color: "#555"
  },
  modalRowValue: {
    fontSize: 16,
    fontWeight: "600",
    color: "#111"
  },
  modalDivider: {
    height: 1,
    backgroundColor: "#E5E7EB",
    marginTop: 8
  },
  bottomSheet: {
    backgroundColor: Colors.white,
    padding: 24,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  modalDOB: {
    fontFamily: "Rubik",
    fontSize: 32,
    fontWeight: "500",
    color: Colors.accountLock,
    textAlign: "center",
    lineHeight: 38,
    marginBottom: 48,
  },
  dobModalFooter: {
    paddingBottom: 24,
    backgroundColor: Colors.white,
  },
  confirmBtn: {
    backgroundColor: Colors.titleTextColor,
    padding: 14,
    borderRadius: 8,
    alignItems: "center",
    height: 48
  },
  footerPopup: {
    // paddingHorizontal: 24,
    paddingBottom: Platform.OS === "ios" ? 24 : 16,
    backgroundColor: Colors.white
  },
  cancelBtn: { paddingTop: 24, alignItems: "center" },
  cancelText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: "500",
    color: Colors.darkGrey,
    lineHeight: 20,
  },
  modalHeaderRow: {
    alignSelf: "flex-end",
    marginBottom: 48,
  },
  modalBodyCenter: {
    alignItems: "center",
    alignSelf: "stretch",
    width: "100%",
  },
  modalInfo: {
    fontFamily: "Rubik",
    fontSize: 14,
    fontWeight: "400",
    color: Colors.accountLock,
    lineHeight: 18,
    marginBottom: 26,
    textAlign: "center",
    alignSelf: "stretch",
    width: "100%",
  },
  gradientFooter: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    backgroundColor: Colors.white,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    ...(Platform.OS === 'web' && {
      position: 'fixed' as any,
      bottom: 0,
      left: 0,
      right: 0,
      width: '100%' as any,
      margin: 0 as any,
      boxSizing: 'border-box' as any,
      paddingBottom: "env(safe-area-inset-bottom, 0px)" as any,
    })
  },
   viewTap: {
    height: 4,
    width: 77,
    backgroundColor: Colors.titleTextColor,
    borderRadius:24,
    // marginBottom: 24,
    alignSelf: 'center'
  },
  gradientContent: {
    paddingHorizontal: 24,
    // paddingVertical: 20,
    alignItems: "center",
  },
  gradientRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    marginBottom: 8,
  },
  gradientFeeLabel: {
    fontSize: 14,
    fontFamily: 'Rubik',
    fontWeight: "400",
    lineHeight: 22,
    color: Colors.titleTextColor,
  },
  gradientPrice: {
    fontSize: 16,
    fontFamily: 'Rubik',
    fontWeight: '500',
    lineHeight: 24,
    color: Colors.titleTextColor,
  },
  gradientContinueButton: {
    width: "100%",
    backgroundColor: Colors.titleTextColor,
    borderRadius: 8,
    paddingVertical: 16,
    alignItems: "center",
    shadowColor: "#000",
    marginBottom: 32,
  },
  gradientContinueButtonText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: "500",
    color: Colors.white,
  },
  oldPrice: {
    fontSize: 16,
    // textDecorationLine: "line-through",
    marginRight: 8,
  },
  newPrice: {
    fontSize: 16,
    fontWeight: "bold",
    color: "green",
  },
});